import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Home } from 'lucide-react';
import Footer from '../components/Footer';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { trackMetaEvent, formatPurchaseData } from '../utils/metaPixel';

const ThankYou = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || searchParams.get('orderId');
  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    window.scrollTo(0, 0);

    // Only track if we have order ID, payment ID, and haven't tracked this order yet
    if (orderId && paymentId && !sessionStorage.getItem(`purchase_tracked_${orderId}`)) {
      const fetchOrderAndTrack = async () => {
        try {
          const orderDoc = await getDoc(doc(db, 'Orders', orderId));

          if (orderDoc.exists()) {
            const orderData = orderDoc.data();

            // Track Purchase event
            const purchaseData = formatPurchaseData(orderData);
            trackMetaEvent('Purchase', {
              ...purchaseData,
              transaction_id: orderId,
            });

            // Mark as tracked to prevent duplicates on refresh
            sessionStorage.setItem(`purchase_tracked_${orderId}`, 'true');

            console.log('[Purchase Tracked]', orderId);
          }
        } catch (error) {
          console.error('Error fetching order for tracking:', error);
        }
      };

      fetchOrderAndTrack();
    }
  }, [orderId, paymentId]);

  return (
    <main className="bg-surface min-h-screen">
      <div className="max-w-screen-lg mx-auto px-6 py-16">
        {/* Success Card */}
        <div className="bg-white rounded-3xl border border-charcoal/10 p-8 md:p-12 text-center animate-fadeInUp">
          {/* Success Icon */}
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping"></div>
            <CheckCircle className="h-20 w-20 text-emerald-500 relative animate-zoomIn" />
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl font-bold mb-4 text-ink">
            Payment Successful!
          </h1>
          <p className="text-xl text-ink-2 mb-8">
            Thank you for your payment. Your order is confirmed!
          </p>

          {/* Order Details */}
          <div className="bg-surface rounded-2xl p-6 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Package className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold">Order Details</h2>
            </div>

            <div className="space-y-3 text-left">
              {orderId && (
                <div className="flex justify-between items-center py-2 border-b border-charcoal/10">
                  <span className="text-ink-3">Order ID:</span>
                  <span className="font-mono font-semibold text-primary">{orderId}</span>
                </div>
              )}
              {paymentId && (
                <div className="flex justify-between items-center py-2 border-b border-charcoal/10">
                  <span className="text-ink-3">Payment ID:</span>
                  <span className="font-mono font-semibold">{paymentId}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2">
                <span className="text-ink-3">Status:</span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold">
                  <CheckCircle className="h-4 w-4" />
                  Paid & Confirmed
                </span>
              </div>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8 max-w-2xl mx-auto text-left">
            <h3 className="font-semibold text-blue-900 mb-3">What happens next?</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>You'll receive a confirmation email with your order details</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>We'll start processing your order within 24 hours</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Track your order status via email notifications</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Your item will be delivered within 3-5 business days</span>
              </li>
            </ul>
          </div>

          {/* Contact Support */}
          <div className="mb-8">
            <p className="text-sm text-ink-3 mb-4">
              Need help? Contact us on WhatsApp
            </p>
            <a
              href="https://wa.me/94771234567"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition font-semibold"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Chat with Support
            </a>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 transition font-semibold"
            >
              <Home className="h-5 w-5" />
              Back to Home
            </button>
            <button
              onClick={() => navigate('/builder')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-charcoal/20 rounded-full hover:bg-surface transition font-semibold"
            >
              Create Another Design
            </button>
          </div>
        </div>

        {/* Footer Thank You Message */}
        <div className="text-center mt-8 text-ink-2">
          <p className="text-lg font-semibold mb-2">
            Thank you for choosing Merch Masters LK!
          </p>
          <p className="text-sm">
            We're excited to bring your design to life.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default ThankYou;
